import { filterForDeployment } from '@avexa/config/demo-mode'
import { FEED_TABS, type FeedTab } from '@avexa/types'

import type { TranslationKey } from '../i18n'

import { showAppSurfaces } from './appSurfaces'

/** Route constants and the nav trees built from them. One place to rename a URL. */

export const routes = {
  home: '/',
  howItWorks: '/how-it-works',
  forIndividuals: '/for-individuals',
  forBusinesses: '/for-businesses',
  pricing: '/pricing',
  categories: '/categories',
  discover: '/discover',
  requests: '/requests',
  deals: '/deals',
  jobs: '/jobs',
  blog: '/blog',
  faq: '/faq',
  support: '/support',
  /**
   * ⚠️ A REAL PAGE SINCE 6 OCT. IT WAS AN ALIAS FOR `/support`, AND THE REASON
   * IT WAS AN ALIAS IS WHY THE PAGE IS SHAPED THE WAY IT IS.
   *
   * Superseded text, for the record: ~~an alias, not a page. The client's
   * reference footer names "Contact"; this site had already built "Support".
   * They are the same page's job and the honest answer to "should there be two?"
   * is no. Two thin pages would each have to admit, separately, that the mailbox
   * does not exist yet (Docs/15 Q7).~~
   *
   * The client asked for "Contact Us" by name in his 29 Sep review § 7. The old
   * objection is answered rather than overruled: `/contact` owns no content of
   * its own and its first card sends the visitor to `/support`, which keeps the
   * help centre and its canonical URL. `nav.support` was relabelled from
   * "Contact & support" to "Help & support" at the same time, because two
   * navigation entries both saying "contact" is exactly the confusion the alias
   * was avoiding.
   *
   * `features/company/data/contact.ts` holds the division of labour.
   */
  contact: '/contact',
  safety: '/safety',
  report: '/report',
  /**
   * 🔴 A STORE REQUIREMENT, AND THE PATH IS ALREADY PUBLISHED — DO NOT RENAME IT.
   *
   * `https://proffplassen.no/delete-account` is pasted into BOTH of Google
   * Play's Data safety URLs — "Account deletion URL" and "Delete data URL" —
   * so the slug is a contract with the Console rather than a choice. Changing
   * it fails the Data safety declaration silently, from Google's side, on a
   * form nobody on this project opens twice.
   *
   * ⚠️ AND IT IS DELIBERATELY NOT BEHIND `showAppSurfaces()`. Nineteen
   * marketing funnels are behind that switch today; a URL a store reviewer and
   * a person exercising a GDPR right both arrive at is not one of them. It must
   * answer in all three states.
   */
  deleteAccount: '/delete-account',
  careers: '/careers',
  about: '/about',
  download: '/download',
  claimBusiness: '/claim-business',
  login: '/login',
  /** Business owners save a card and start the trial. Linked from email, never from the app (Apple 3.1.1). */
  billing: '/billing',
  privacy: '/legal/privacy',
  terms: '/legal/terms',
  cookies: '/legal/cookies',
} as const

export const directoryPath = (locale: string, city: string, category: string): string =>
  `/${locale}/${city}/${category}`

export const businessPath = (slug: string): string => `/business/${slug}`

/**
 * One open customer request.
 *
 * Addressed by ID rather than by a slug, because `requests` has no slug column
 * and inventing one from the title would publish the title twice — once in the
 * URL, where it cannot be edited or withdrawn afterwards. A request is also the
 * one thing on this site whose text belongs to a private individual, so a URL
 * that reads back "rewire-my-bathroom-in-grunerlokka" is worse than an opaque
 * one even though both are public.
 */
export const requestPath = (id: string): string => `${routes.requests}/${id}`

/**
 * ─── DISCOVER URL VOCABULARY ────────────────────────────────────────────────
 *
 * The feed's tab lives in the query string, not in component state, so that a
 * tab is linkable and the back button returns to the previous tab instead of
 * leaving the page.
 *
 * TWO SPELLINGS, ON PURPOSE, AND THIS FILE IS THE ONLY PLACE BOTH EXIST.
 * The canonical value is `FeedTab` from `@avexa/types` — `for_you`, the same
 * literal the database, `@avexa/api` and both other apps use. The URL wants
 * `for-you`, because a hyphen is what a reader, a crawler and every other URL on
 * this site use between words. Neither spelling is allowed to leak into the
 * other's territory: components and hooks take `FeedTab`, `<a href>` takes the
 * slug, and the two mappings below are the border crossing. The slugs are a
 * public contract — changing one breaks every link anybody has shared.
 */
export const DISCOVER_TAB_SLUGS = {
  for_you: 'for-you',
  nearby: 'nearby',
  following: 'following',
} as const satisfies Record<FeedTab, string>

export type DiscoverTabSlug = (typeof DISCOVER_TAB_SLUGS)[FeedTab]

export const DEFAULT_DISCOVER_TAB: FeedTab = 'for_you'

export const toDiscoverTabSlug = (tab: FeedTab): DiscoverTabSlug => DISCOVER_TAB_SLUGS[tab]

/**
 * URL → the canonical tab. Anything unrecognised — a typo, an old link, `null`
 * — is the default tab rather than a 404: a bad query parameter should not cost
 * a visitor the page.
 *
 * The canonical spelling is accepted too, so a link somebody builds straight
 * from `FEED_TABS` still lands on the right tab. It is not what this app emits:
 * `discoverPath` is the only thing that writes the parameter, and it always
 * writes the slug.
 */
export const parseDiscoverTab = (raw: string | null | undefined): FeedTab =>
  FEED_TABS.find((tab) => DISCOVER_TAB_SLUGS[tab] === raw || tab === raw) ?? DEFAULT_DISCOVER_TAB

/**
 * `/discover`, `/discover?tab=nearby&city=Oslo`, `/discover?tab=following`.
 *
 * The default tab is the BARE path rather than `?tab=for-you`: two URLs for the
 * front page of the feed would be two entries in the index competing with each
 * other. `?tab=for-you` typed by hand still resolves — it is just not the one
 * this app ever links to or declares canonical.
 *
 * `city` is the display name (`Bærum`), not a slug: `listNearby` matches
 * `posts.city`, and that column stores the name.
 */
export const discoverPath = (tab: FeedTab = DEFAULT_DISCOVER_TAB, city?: string): string => {
  const params = new URLSearchParams()
  if (tab !== DEFAULT_DISCOVER_TAB) params.set('tab', toDiscoverTabSlug(tab))
  if (tab === 'nearby' && city) params.set('city', city)
  const query = params.toString()
  return query ? `${routes.discover}?${query}` : routes.discover
}

/** A community question and its answers — the indexable long-tail page. */
export const questionPath = (id: string): string => `${routes.discover}/q/${id}`

/**
 * Any other Discover post: a poll, a guess, a list, a recommendation, or one of
 * the five business kinds.
 *
 * Addressed by SLUG where there is one, and by id where there is not — a
 * person's post never gets a slug, because nothing in the compose flow
 * generates one. Which of the two a given item uses is `feedItemHref`'s
 * decision (`features/discover/lib/routes.ts`); this only builds the path.
 */
export const postPath = (slugOrId: string): string => `${routes.discover}/p/${slugOrId}`

/**
 * A business's long-form article lives under `/blog`, not under Discover: the
 * same piece reached from the feed and from the blog index has to be ONE url or
 * the two split their own SEO.
 */
export const blogPostPath = (slug: string): string => `${routes.blog}/${slug}`

export const topicPath = (slug: string): string => `${routes.discover}/topic/${slug}`

export type NavItem = {
  to: string
  labelKey: TranslationKey
  /**
   * Is the destination worth putting in front of the client?
   *
   * On the client-facing demo build (VITE_DEMO_MODE=true) anything without this
   * is dropped from the nav, so the client is shown a small site that works
   * rather than a large one that mostly does not. The ROUTE still exists — this
   * hides navigation, not data, and is not a security boundary. See
   * `packages/config/src/demo-mode.ts`.
   *
   * ⚠️ IT NO LONGER MEANS "NOT A PLACEHOLDER". `ComingSoon` is deleted and every
   * route in Docs/07's map renders something real, so `built: false` is now a
   * judgement about whether a finished page belongs in the navigation — see
   * `/pricing` below, which is finished, honest, `noIndex`, and deliberately not
   * advertised.
   */
  built?: boolean
  /**
   * A grouped menu rather than a link. The header renders a dropdown; the item's
   * own `to` is still a real destination, so the label is never a dead end for
   * a keyboard user or a crawler.
   */
  children?: NavItem[]
}

/** Drops unfinished destinations on the demo build; a no-op everywhere else. */
const forDeployment = (items: NavItem[]): NavItem[] =>
  filterForDeployment(items, (item) => item.built === true)

/**
 * The browse-y destinations — "what is here?" — as one list, because they are
 * now one MENU in the header and one COLUMN in the footer, and having them in
 * two places is how the two silently diverge.
 */
const browseDestinations: NavItem[] = [
  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * 🔴 THREE ENTRIES ARE GATED ON `showAppSurfaces()` — 12 October 2026
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * Requests, Deals and Categories appear only when `lib/appSurfaces.ts` is not
   * `'hidden'`. **Do not edit this array to change that** — flip the one line in
   * that file, which also moves the ten other places this decision reaches.
   *
   * THE ARGUMENT, PRESERVED VERBATIM, because it is the argument every time
   * this comes up: none of these was ever asked for by name. Counted across all
   * four of the client's documents, "Deals" appears 45 times and EVERY ONE is
   * inside the app — "appear in the customer's Discover → Deals section", "a
   * preview of selected deals directly on Discover". His only list of website
   * pages is `Feedback from Hassan.docx` § 7, "WEBSITE – NEW PAGES / TABS", and
   * it names exactly two: Home and Contact Us. On 12 Oct he confirmed it
   * directly — he does not need them yet, and they return as a priced option.
   *
   * ⚠️ ONE CLAIM REMAINS UNVERIFIED AND MUST NOT BE RESTATED AS FACT. An older
   * comment here said "the client's own footer lists Deals", citing
   * `Nettside - Example.pdf`. The PDF's text is CID-encoded and does not
   * extract, so nobody has been able to re-read that footer. It may well be
   * true — but a comment is not evidence, and this repository has been wrong
   * three times by treating one as though it were. **If somebody opens that PDF
   * and confirms it, that is a real argument for unhiding — say so.**
   *
   * `/deals` is an EXPLAINER, not a listing — one promotion in the database and
   * no way to claim one from the web (Docs/15 N14). The page says so.
   */
  ...(showAppSurfaces()
    ? [
        { to: routes.requests, labelKey: 'nav.requests', built: true } as NavItem,
        { to: routes.deals, labelKey: 'nav.deals', built: true } as NavItem,
        { to: routes.categories, labelKey: 'nav.categories', built: true } as NavItem,
      ]
    : []),
  /*
   * ⚠️ DISCOVER WAS HERE AND WAS REMOVED ON 23 SEP.
   *
   * `Docs/27` § 1 removed posting, and on this website `/discover` was the
   * community feed and nothing else. The ROUTE still exists and renders
   * `RetiredDiscoverPage` so that an indexed URL or an old link explains itself
   * — but a retired page does not belong in a menu of places to go, which is
   * exactly the distinction `/jobs` draws: routed, reachable, not listed.
   *
   * To put it back:
   *   { to: routes.discover, labelKey: 'discover.title', built: true },
   * labelled from `discover.title` rather than a `nav.*` key, so the feed's name
   * stays defined once in the namespace the whole feature reads from.
   *
   * ⚠️ AND MEASURE THE HEADER IN NORWEGIAN WHEN YOU DO — see the note under
   * `headerNav` below. This row has been within fifteen pixels of pushing the
   * primary call to action off the right edge.
   */
  /*
   * Real articles from `posts` where `kind = 'article'` — `features/blog`.
   *
   * ⚠️ IT IS THE ONLY UNCONDITIONAL ENTRY, AND THAT IS DELIBERATE. The blog
   * stayed visible through both hidings: real articles, written by businesses,
   * under their own byline. It is ordinary content, not a window into the app's
   * data, so it is not part of what is being sold back.
   */
  { to: routes.blog, labelKey: 'nav.blog', built: true },
]

/**
 * The contents of the header's "Explore" menu.
 *
 * ⚠️ NOTHING READS THIS WHILE `appSurfaces` IS `'hidden'`, and that is correct
 * rather than dead code: `primaryNav` renders a plain Blog link instead, for the
 * reason set out at that branch. Visible, it holds four — Requests, Deals,
 * Categories, Blog — and becomes the dropdown's contents. `Header.tsx` branches
 * on `item.children` and has never been touched by any of this.
 */
export const headerExploreNav: NavItem[] = forDeployment(browseDestinations)

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HEADER NAV — Docs/07 § 8, RESTRUCTURED 22 SEP TO THE CLIENT'S OWN HEADER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Their header, left to right: wordmark · How it works · For Customers ·
 * For Businesses · Explore Avexa · About. (Ours drops the product name from
 * "Explore" — see the measurement at `nav.explore` below.)
 *
 * ─── "EXPLORE" IS NOT A COSMETIC CHANGE — IT IS WHAT BOUGHT THE ROOM ───────
 *
 * This nav used to be SIX flat links (Categories, Discover, For individuals,
 * For businesses, Blog, Requests) and `Header.tsx` measured the desktop row at
 * **1265 px against a 1280 px breakpoint**. Fifteen pixels. That is why
 * `/how-it-works` — the first item in the client's own nav — could not be
 * added, and the comment below `exploreNav` recorded it as a measurement rather
 * than an oversight.
 *
 * The client's answer is structural: they have five top-level items because the
 * browse-y destinations are GROUPED. Doing the same here collapses five links
 * into one menu label and pays for `How it works` and `About` outright.
 *
 * ⚠️ IF A LINK IS EVER ADDED BACK, MEASURE IT IN A BROWSER, IN NORWEGIAN.
 * Norwegian is the longer locale ("Slik fungerer det" is ~80 px wider than the
 * English) and the control being pushed off the right edge is the primary CTA —
 * the single most important thing on a marketing site for an unreleased app.
 *
 * ─── TWO THINGS FROM THEIR HEADER THAT ARE DELIBERATELY NOT HERE ───────────
 *
 * • "For Business" — an outlined pill, SEPARATE from the "For Businesses" link,
 *   so it is the business-side entry point rather than the marketing page. We
 *   have no business sign-in on the web (the app is where a business works), so
 *   the pill would be a button with nowhere to go. Left out until there is a
 *   destination. See `Docs/15`.
 * • "Get Early Access" IS here, as the primary CTA, pointing at `/download` —
 *   see `Header.tsx` and `Docs/15` N28 for why it collects nothing.
 *
 * `/pricing` remains out of the header, and that is unchanged and separate: it
 * is a finished, honest page with NO PRICES ON IT (no `plans` table; the
 * client's payment module gives two Boost price lists, two Avexa+ prices and
 * both two and three sponsored slots — Docs/15 N20–N22). A header link labelled
 * "Pricing" promises a price list. It lives in the footer's company column and
 * is `noIndex` either way. Put it back in the same commit that gives `plans`
 * real rows.
 */
export const primaryNav: NavItem[] = forDeployment([
  { to: routes.howItWorks, labelKey: 'nav.howItWorks', built: true },
  /*
   * ⚠️ "For Individuals", NOT "For Customers", AND THIS REVERSES AN EARLIER
   * DECISION ON THE CLIENT'S OWN INSTRUCTION.
   *
   * The label used to be "For Customers" because the client's 22 Sep reference
   * header said "For Customers" and it was read as their product's vocabulary.
   * Their 29 Sep review (§ 3.1) is explicit in the other direction: *"When
   * opening the side menu, it currently uses 'Customers'. We should not use the
   * word 'Customers' here. Change it to: For Individuals."* The later
   * instruction wins.
   *
   * The URL does not move. `/for-individuals` is indexed and was always the
   * honest slug; only the label was ever "Customers", which is part of why the
   * mismatch was worth fixing rather than entrenching.
   *
   * ⚠️ "Customer" has NOT been purged from the site, and must not be. It is the
   * correct word for one PARTY IN A TRANSACTION — "the customer accepts the
   * offer", "customers find, businesses grow" — and the client uses it that way
   * himself throughout the same document. What he objected to is naming an
   * AUDIENCE "Customers" in navigation, because a person browsing is not yet
   * anybody's customer. Keep the distinction: audience labels say individuals,
   * prose about a job says customer.
   */
  { to: routes.forIndividuals, labelKey: 'nav.forIndividuals', built: true },
  { to: routes.forBusinesses, labelKey: 'nav.forBusinesses', built: true },
  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * 🔴 A DROPDOWN OR A PLAIN LINK, DECIDED BY THE SAME SWITCH — Docs/34 § 1b.2
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * ⚠️ THIS IS ASYMMETRY #1, THE ONE THE DOCUMENT SAYS SOMEBODY WILL MISS, AND
   * IT IS NOW A BRANCH INSTEAD OF A MANUAL STEP.
   *
   * Hidden, `browseDestinations` falls to a SINGLE entry — Blog — and a dropdown
   * over one item is strictly worse than the link: two interactions instead of
   * one, a chevron promising a choice that does not exist, and on a keyboard a
   * panel to open before anything is reachable. So Blog is PROMOTED to a
   * top-level `NavLink`. Visible, it is demoted back into the menu and this slot
   * is the grouped item.
   *
   * It was done by hand twice, in opposite directions, and both times the
   * promotion was the step that had to be written down separately because
   * deleting the group alone **loses `/blog` from the header entirely**.
   *
   * ⚠️ `Header.tsx` IS UNTOUCHED BY EITHER STATE. It branches on
   * `item.children`, so `ExploreMenu` and all of its hover, keyboard and touch
   * handling are intact and come back the moment an item has children again.
   *
   * ⚠️ THE ROW GETS NARROWER WHEN HIDDEN, WHICH IS THE SAFE DIRECTION.
   * "Blog"/"Blogg" is shorter than "Explore"/"Utforsk" in both locales and the
   * chevron goes. Still five items either way. The standing warning above —
   * this row has measured 1265 px against a 1280 px breakpoint, and the control
   * that goes off the right edge first is the primary CTA — applies to the next
   * change in the WIDENING direction.
   *
   * ⚠️ `nav.explore` IS "Explore" / "Utforsk" AND MUST NOT GET THE PRODUCT NAME
   * BACK. It read "Explore {{product}}", mirroring the client's own header
   * ("Explore Avexa"). The rename to Proffplassen turned five characters into
   * twelve: measured at 1280 px in Norwegian, "Utforsk Proffplassen" WRAPPED THE
   * CAPSULE TO TWO LINES, every label breaking mid-phrase ("Slik fungerer /
   * det"). English never wrapped, so it is invisible unless you check the longer
   * locale.
   */
  showAppSurfaces()
    ? {
        to: routes.categories,
        labelKey: 'nav.explore',
        built: true,
        children: headerExploreNav,
      }
    : { to: routes.blog, labelKey: 'nav.blog', built: true },
  { to: routes.about, labelKey: 'nav.about', built: true },
])

/**
 * ─── THE FOOTER IS THE SITE MAP, AND IT IS NOW THREE COLUMNS OF IT ──────────
 *
 * The client's feedback of 22 Sep asks for "a more complete structure … multiple
 * dedicated pages/tabs rather than feeling like a «similar web-app»". Their own
 * reference footer lists fourteen destinations; the header cannot hold fourteen
 * and should not try, so the footer carries the structure and the header carries
 * the six journeys.
 *
 * The three lists below are grouped by the QUESTION a visitor is asking, not by
 * who owns the page internally:
 *
 *   exploreNav — "what is here?"          the marketplace itself
 *   companyNav — "who are you?"           the company and the ways in
 *   helpNav    — "what if it goes wrong?" help, trust and the legal documents
 *
 * ✅ `/how-it-works` IS NOW IN THE HEADER TOO, and it is still listed here.
 * It could not be before: the flat six-link row measured 1265 px against a
 * 1280 px breakpoint. Grouping the browse destinations under "Explore" freed
 * the space — see `primaryNav`. The footer keeps it because the footer is the
 * site map and a visitor reading the bottom of a page should not have to scroll
 * back up.
 *
 * ⚠️ THIS COLUMN IS THREE ENTRIES WHILE `appSurfaces` IS `'hidden'` — Home, How
 * it works, Blog — and six when it is not. It is thin, not broken: **the heading
 * is the QUESTION ("what is here?"), not a count.** It needs no special-casing
 * because it spreads `browseDestinations`, which already carries the switch.
 *
 * `Footer.tsx`'s own two data-backed columns (categories, cities) are gated
 * separately in that file, along with the grid track they change.
 */
/**
 * ⚠️ `Home` IS FIRST, AND IT IS THERE BECAUSE THE CLIENT ASKED FOR IT BY NAME.
 *
 * § 7, "WEBSITE – NEW PAGES / TABS": *"The following pages should be added:
 * Home. Contact Us."* `Contact Us` became `/contact` on 6 Oct and is the first
 * entry in `helpNav` below. `Home` was read as already satisfied — the route
 * exists, it is the index — and that reading missed half of what he asked for:
 * **it appeared in no navigation anywhere on the site.** The logo links to it,
 * which a desktop reader understands and a reader in the side menu does not.
 *
 * ⚠️ IT IS DELIBERATELY NOT IN `primaryNav`. That row measured 1265 px against
 * a 1280 px breakpoint before "Explore" bought the space back, the control that
 * goes off the right edge first is the primary CTA, and Norwegian is the longer
 * locale — see the warning above `primaryNav`. "Home"/"Hjem" is short, but a
 * sixth desktop item is exactly the change that warning exists for and it
 * cannot be measured from this machine. It is in the FOOTER site map (here) and
 * as an explicit first row of the MOBILE sheet (`components/layout/Header.tsx`),
 * which is the menu he was looking at when he wrote § 3.1 about the same menu.
 */
export const exploreNav: NavItem[] = forDeployment([
  { to: routes.home, labelKey: 'nav.home', built: true },
  { to: routes.howItWorks, labelKey: 'nav.howItWorks', built: true },
  ...browseDestinations,
])

/**
 * Who we are and the ways in. `/pricing` is the one entry deliberately NOT in
 * the header — see the note in `primaryNav`. It is listed rather than hidden
 * because "what will this cost me" is a question a business owner goes looking
 * for an answer to, and "not settled yet, and here is what it will cover" is a
 * better answer than a missing link.
 *
 * `built: true` on `/pricing` is about the FOOTER and the demo build, not about
 * the page having prices. It does not.
 *
 * ⚠️ `/careers` is {{company}}'s, not the product's, and it has no open roles.
 * The page says exactly that. It is here because the client's reference footer
 * has "Careers" and because a careers link that leads to an honest "not yet" is
 * the cheapest possible version of one.
 */
export const companyNav: NavItem[] = forDeployment([
  { to: routes.about, labelKey: 'nav.about', built: true },
  { to: routes.careers, labelKey: 'nav.careers', built: true },
  { to: routes.pricing, labelKey: 'nav.pricing', built: true },
  // Where a business saves its card and starts the trial. In the footer because
  // the app may not link to it (Apple 3.1.1), so the website has to be findable.
  { to: routes.billing, labelKey: 'nav.billing', built: true },
  { to: routes.claimBusiness, labelKey: 'nav.claimBusiness', built: true },
  { to: routes.forBusinesses, labelKey: 'nav.forBusinesses', built: true },
  { to: routes.download, labelKey: 'nav.download', built: true },
])

/**
 * Help, trust and the legal documents, in one column because they are one
 * question: what happens when something goes wrong.
 *
 * ⚠️ `built: true` on the last three means THE PAGE EXISTS, not that the text is
 * approved. All three are drafts that no lawyer has read, they say so in a
 * banner at the top, and they are served `noindex`. The flag controls the demo
 * build's nav (see `NavItem.built`) — it is not a statement about legal review,
 * and the pages must not be treated as in force until `Docs/15` Q27 and Q28 are
 * answered and a Norwegian lawyer has signed the text off.
 *
 * ⚠️ TWO ENTRIES FROM THE CLIENT'S REFERENCE FOOTER ARE DELIBERATELY ABSENT.
 * "Marketing" is sponsored placement and Boost — the payment module, whose own
 * source document gives two contradicting price lists (Docs/15 N20–N24), so a
 * page about it would have to invent which one is true. "Business Terms" is a
 * second legal instrument, and the first three already need a Norwegian lawyer
 * before they mean anything. Neither is a page we can honestly write today.
 */
export const helpNav: NavItem[] = forDeployment([
  /*
   * ⚠️ "Contact us" IS FIRST AND `/support` IS SECOND, WHICH IS THE OPPOSITE OF
   * HOW THEY WERE BUILT. The client asked for a Contact Us page by name (§ 7),
   * and a visitor looking for a way to reach a human looks for the word
   * "contact" — which is why `/contact` existed as a redirect for two weeks.
   * It is now the switchboard and `/support` is the help centre behind it;
   * `features/company/data/contact.ts` has the division of labour.
   */
  { to: routes.contact, labelKey: 'nav.contact', built: true },
  { to: routes.support, labelKey: 'nav.support', built: true },
  { to: routes.faq, labelKey: 'nav.faq', built: true },
  { to: routes.safety, labelKey: 'nav.safety', built: true },
  { to: routes.report, labelKey: 'nav.report', built: true },
  /*
   * ⚠️ IT IS IN THE FOOTER BECAUSE A STORE REVIEWER LOOKS FOR IT THERE, AND
   * BECAUSE THIS COLUMN IS NOT GATED.
   *
   * Google Play's Data safety form holds this URL twice over (account deletion
   * and partial data deletion), and a URL that is reachable only by typing it
   * reads as a page produced for the form. `helpNav` is also the one column
   * `appSurfaces` never touches, which is the property this entry needs —
   * see the note on `routes.deleteAccount`.
   *
   * It sits between `report` and the three legal documents on purpose: it is
   * the last of the "what if it goes wrong?" actions and the first thing that
   * is partly a legal instrument.
   */
  { to: routes.deleteAccount, labelKey: 'nav.deleteAccount', built: true },
  { to: routes.privacy, labelKey: 'nav.privacy', built: true },
  { to: routes.terms, labelKey: 'nav.terms', built: true },
  { to: routes.cookies, labelKey: 'nav.cookies', built: true },
])

export type SocialLink = {
  id: string
  href: string
  /** Icon name resolved through `lib/icons.ts`. */
  icon: string
  /** Network names are proper nouns. */
  name: string
}

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 EMPTY SINCE 13 OCT, AND IT MUST STAY EMPTY UNTIL THE ACCOUNTS EXIST
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * These three shipped on every page of the live site, in both languages, and
 * **every one of them sent a visitor to a stranger**:
 *
 *   instagram.com/avexa          "Elaine (@avexa)" — a private individual.
 *                                Verified by fetching the page title, 13 Oct.
 *   linkedin.com/company/avexa   an unrelated company called Avexa, which
 *                                provides business education. 83 followers.
 *   facebook.com/avexa           an `avexa` page that is not this product's.
 *
 * ⚠️ AND THEY WERE WRONG BY CONSTRUCTION, NOT BY BAD LUCK. They were written
 * when the product was called Avexa. **The product was renamed to Proffplassen
 * on 22 September** (`packages/config/src/brand.ts` — the company keeps its
 * name, the product does not), and nobody has ever owned an `avexa` handle on
 * any network. So the rename turned three plausible links into three pointers
 * at other people's profiles, and `Docs/26`'s rename checklist did not list
 * them.
 *
 * 🔴 WHY THEY ARE DELETED RATHER THAN POINTED SOMEWHERE ELSE: there is nowhere
 * to point them. No Proffplassen account exists on any network. **A guessed
 * `proffplassen` handle would be the same defect with a newer spelling** — and
 * worse, it would look checked.
 *
 * This is the same rule as the Apple mark in `ProviderButton.tsx`: *a missing
 * mark is a gap somebody notices and fills; a wrong one looks finished and
 * fails quietly.* An absent row of icons is a footer nobody questions. A row
 * that opens a stranger's Instagram reads as a broken brand at best and an
 * impersonation risk at worst.
 *
 * ⚠️ `Footer.tsx` RENDERS NOTHING WHEN THIS IS EMPTY — the whole block is
 * behind a length check, so there is no orphaned heading and no empty row. Add
 * a real account and it comes back by itself.
 *
 * 🔴 BEFORE ADDING ONE: open the URL. Not the handle — the URL, in a browser,
 * and confirm whose profile answers. That is the check nobody ran here.
 */
export const socialLinks: SocialLink[] = []

/**
 * A Norwegian slug: `Bærum` → `baerum`, `Tromsø` → `tromso`.
 * Good enough for the scaffold's search submit; the real thing resolves the
 * "where" field against Kartverket and the "what" field against `categories`.
 */
export const slugify = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .normalize('NFD')
    .replace(new RegExp('[\u0300-\u036f]', 'g'), '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
